import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, MediaGallery, type MediaGalleryItem } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { EmptyState } from '@/components/feedback/EmptyState';
import { formatFileSize, formatMediaFormat } from '@/lib/formatters';
import { useClientId } from '@/features/value/hooks/useClientId';
import { useProductDetail } from '@/features/completeness/hooks/useCompleteness';
import { useProductMedia } from '../hooks/useProductMedia';
import { downloadProductMediaItem, downloadProductZip, triggerBlobDownload, ZipLimitExceededError } from '../media.api';

interface ProductMediaLocationState {
  /** Best-effort hint carried from the product-list thumbnail click (FR56); absent on a direct URL visit/refresh. */
  brand?: string;
}

/**
 * Product-media page reached by clicking a thumbnail in the customer product list
 * (FR14a). Shows every known media item for one GTIN, grouped by type, with per-file,
 * selection-ZIP and download-all actions via the mediaserver bulk faciliteit.
 */
export function ProductMediaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams<{ productId: string }>();
  const gln = useClientId();
  const { data: detailData, isError: detailIsError, error: detailError, refetch: refetchDetail } = useProductDetail(gln, productId ?? '');
  const detail = detailData?.data;
  const gtin = detail?.gtins[0]?.gtin ?? '';
  const productName = detail?.productName ?? '';
  const brand = (location.state as ProductMediaLocationState | null)?.brand;

  const { items, degraded, isLoading, isError: mediaIsError, error: mediaError, refetch: refetchMedia } = useProductMedia({ gln, gtin }, gtin !== '');
  const showLoading = gtin === '' || isLoading;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [zipLimitExceeded, setZipLimitExceeded] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Drop selections that disappeared from the product's media (e.g. a background
  // refetch). The archive route re-validates ownership server-side regardless; this
  // only keeps the bulkbar's count/state honest.
  useEffect(() => {
    const knownIds = new Set(items.map((item) => item.mediaId));
    setSelectedIds((previous) => {
      const next = new Set([...previous].filter((id) => knownIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [items]);

  const toggleSelection = (mediaId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(mediaId)) next.delete(mediaId);
      else next.add(mediaId);
      return next;
    });
  };

  const handleDownload = async (item: MediaGalleryItem) => {
    if (isDownloading) return;
    setDownloadFailed(false);
    setIsDownloading(true);
    try {
      await downloadProductMediaItem({ gln, gtin }, item.mediaId, item.fileName);
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadZip = async (mediaIds: string[]) => {
    if (isDownloading) return;
    setZipLimitExceeded(false);
    setDownloadFailed(false);
    setIsDownloading(true);
    try {
      const blob = await downloadProductZip({ gln, gtin, mediaIds });
      triggerBlobDownload(blob, `media-${gtin}.zip`);
    } catch (error) {
      if (error instanceof ZipLimitExceededError) setZipLimitExceeded(true);
      else setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const typeBadgeLabel = (bucket: string) => t(`media.library.types.${bucket}`, { defaultValue: t('media.library.types.overig') });

  const labels = {
    groupLabel: (typeInfo: string) => t(`media.gallery.groups.${typeInfo.toUpperCase()}`, { defaultValue: t('media.gallery.groups.UNKNOWN') }),
    emptyState: t('products.media.emptyState'),
    imageAlt: (fileName: string) => t('media.gallery.imageAlt', { fileName }),
    enlarge: (fileName: string) => t('media.gallery.enlarge', { fileName }),
    download: (fileName: string) => t('media.gallery.download', { fileName }),
    dialogTitle: (fileName: string) => t('media.gallery.dialogTitle', { fileName }),
    close: t('common.close'),
    placeholder: t('media.gallery.placeholder'),
    select: (fileName: string, selected: boolean) => t(selected ? 'media.gallery.deselect' : 'media.gallery.select', { fileName }),
    // Per-tile type badge and filter tabs (h2-4 mockup product-media.html): reuses the
    // DAM library's already-translated packshot/etiket/sds/marketing/overig taxonomy
    // instead of introducing a second, competing set of labels.
    typeBadge: typeBadgeLabel,
    metaLine: (item: MediaGalleryItem) => {
      const parts: string[] = [];
      if (item.width != null && item.height != null) parts.push(`${item.width}×${item.height}`);
      const format = formatMediaFormat(item.mimeType ?? null);
      if (format != null) parts.push(format);
      if (item.size != null) parts.push(formatFileSize(item.size));
      return parts.length > 0 ? parts.join(' · ') : null;
    },
    tabLabel: (bucket: string | null, count: number) => (
      bucket == null
        ? t('products.media.tabs.all', { count })
        : t('products.media.tabs.byType', { type: typeBadgeLabel(bucket), count })
    ),
    tabGroupLabel: t('products.media.tabs.groupLabel'),
  };

  return (
    <div className="space-y-5" data-testid="product-media-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button aria-label={t('products.media.back')} onClick={() => navigate(-1)} size="icon" variant="ghost">
            <span aria-hidden="true" className="material-symbols-outlined">arrow_back</span>
          </Button>
          <div>
            {brand != null && brand !== '' && (
              <p className="text-[0.8125rem] text-[var(--foreground-muted)]">{brand}</p>
            )}
            <h1 className="text-[1.25rem] font-bold tracking-[-0.02em] text-[var(--foreground-heading)]">
              {productName}
            </h1>
            {gtin !== '' && (
              <p className="mt-[3px] text-[0.8125rem] text-[var(--foreground-muted)]">
                {t('products.media.gtinLabel', { gtin })}
              </p>
            )}
          </div>
        </div>
        {gtin !== '' && (
          <Link className="flex items-center gap-1 text-sm font-medium text-[var(--secondary)] hover:underline" to={`/media?gtin=${encodeURIComponent(gtin)}`}>
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">perm_media</span>
            {t('products.media.viewInLibrary')}
          </Link>
        )}
      </div>

      {detailIsError && <ApiErrorMessage error={detailError} onRetry={() => void refetchDetail()} />}

      {showLoading && !degraded && !detailIsError && (
        <p className="text-sm text-[var(--foreground-muted)]">{t('common.loading')}</p>
      )}

      {degraded && <EmptyState icon="info" title={t('products.media.degraded')} variant="neutral" />}

      {!degraded && mediaIsError && <ApiErrorMessage error={mediaError} onRetry={() => void refetchMedia()} />}

      {zipLimitExceeded && (
        <div className="rounded-[10px] border border-[rgba(214,69,69,0.25)] bg-[var(--destructive-light)] px-4 py-3 text-sm text-[var(--foreground)]" role="alert">
          {t('products.media.zipLimitExceeded')}
        </div>
      )}

      {downloadFailed && (
        <div className="rounded-[10px] border border-[rgba(214,69,69,0.25)] bg-[var(--destructive-light)] px-4 py-3 text-sm text-[var(--foreground)]" role="alert">
          {t('errors.generic')}
        </div>
      )}

      {!degraded && !mediaIsError && !showLoading && items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3" data-testid="media-actions">
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2" data-testid="media-bulkbar">
              <span className="text-sm text-[var(--foreground-muted)]">
                {t('products.media.selectedCount', { count: selectedIds.size })}
              </span>
              <Button disabled={isDownloading} onClick={() => void downloadZip([...selectedIds])} size="sm" variant="outline">
                {t('products.media.downloadSelection')}
              </Button>
            </div>
          )}
          <Button disabled={isDownloading} onClick={() => void downloadZip(items.map((item) => item.mediaId))} size="sm" variant="outline">
            {t('products.media.downloadAll')}
          </Button>
        </div>
      )}

      {!degraded && !mediaIsError && !showLoading && (
        <MediaGallery
          items={items}
          labels={labels}
          layout="tabs"
          onDownload={handleDownload}
          selection={{ selectedIds, onToggle: toggleSelection }}
        />
      )}
    </div>
  );
}
